import { type } from '@ngrx/signals';
import { eventGroup } from '@ngrx/signals/events';
import { ErrorCodeEnum } from '@core/enums/error-code.enum';
import { NServicesHttpErrorResponse } from '@core/models/custom-http-error.model';
import { IUser } from '@core/models/interfaces/user.interface';
import { IHydratedSession, ILoginError, ITokenData } from '@core/store/core.models';
import { ITimebank } from '@shared/models/interfaces/timebank.interface';
import { IWeekWorktime } from '@shared/models/interfaces/week-worktime.interface';

/** Session: hydration from localStorage and token renewal. */
export const sessionEvents = eventGroup({
  source: 'Session',
  events: {
    hydrated: type<IHydratedSession>(),
    /** Tokens are available in the session: the user can be retrieved. */
    tokenAvailable: type<void>(),
    /** `?code=` is present in the URL: it must be exchanged for a token. */
    codeReceived: type<string>(),
    tokenRefreshed: type<ITokenData>(),
    /** Failure exchanged/refreshing the token: carries the error to render on the login page. */
    tokenExchangeFailed: type<ILoginError | undefined>(),
    tokenRefreshFailed: type<ILoginError | undefined>()
  }
});

/** Outcomes of the authentication and profile backend calls. */
export const authApiEvents = eventGroup({
  source: 'Auth API',
  events: {
    tokenGenerated: type<ITokenData>(),
    tokenGenerationFailed: type<NServicesHttpErrorResponse>(),
    userLoaded: type<IUser>(),
    userLoadFailed: type<void>(),
    /** No token and no code in the URL: anonymous user. */
    anonymous: type<void>(),
    loginErrorReceived: type<ILoginError>(),
    worktimeLoaded: type<IWeekWorktime[]>(),
    timebankLoaded: type<ITimebank>(),
    logoutCompleted: type<void>()
  }
});

/** User intents coming from the auth pages. */
export const authPageEvents = eventGroup({
  source: 'Auth Page',
  events: {
    authorizeRequested: type<{ loginHint?: string, prompt?: string }>(),
    logoutRequested: type<void>(),
    dashboardRequested: type<void>(),
    /**
     * Login redirect: resets the session and, when triggered by an authentication failure,
     * carries the error to show on the login page (`undefined` = silent redirect, e.g. from
     * the error page).
     */
    loginRedirectRequested: type<ILoginError | undefined>()
  }
});

/** Authorization refused: `denied` from the guards, `revoked` from the interceptor. */
export const accessEvents = eventGroup({
  source: 'Access',
  events: {
    denied: type<ErrorCodeEnum>(),
    revoked: type<ErrorCodeEnum>()
  }
});
